import { DataGrid } from 'devextreme-react';
import {
  Column,
  Editing,
  Form,
  Lookup,
  Popup,
  RequiredRule,
} from 'devextreme-react/data-grid';
import React, { useCallback, useState } from 'react';
import serviceCall from '../../store/serviceCall';
import DataSource from 'devextreme/data/data_source';
import CustomStore from 'devextreme/data/custom_store';
import { Item } from 'devextreme-react/form';
import { ToasterMessage } from '../../components/sharedComponents';

const SsoConfigPage = (): JSX.Element => {
  const [ssoConfigData] = useState(
    new DataSource({
      store: new CustomStore({
        key: 'pkId',
        load: () => serviceCall.getData('CustomerExternalAuthConfig'),
        insert: values =>
          serviceCall
            .postDataNew('CustomerExternalAuthConfig', values)
            .then(response => {
              if (response.status === 200) {
                ToasterMessage('success', 'SSO config added successfully');
              } else {
                ToasterMessage(
                  'error',
                  `Failed to add the SSO config: ${response}`,
                );
              }
            }),
        update: (key, values) =>
          serviceCall
            .patch(`CustomerExternalAuthConfig/${key}`, values)
            .then(response => {
              if (response.status === 200) {
                ToasterMessage('success', 'SSO config updated successfully');
              } else {
                ToasterMessage(
                  'error',
                  `Failed to update the SSO config: ${response}`,
                );
              }
            }),
        remove: key =>
          serviceCall
            .deleteDataWithoutData(`CustomerExternalAuthConfig/${key}`)
            .then(response => {
              if (response.status === 200) {
                ToasterMessage('success', 'SSO config deleted successfully');
              } else {
                ToasterMessage(
                  'error',
                  `Failed to delete the SSO config: ${response}`,
                );
              }
            }),
      }),
    }),
  );

  const [customerLookupData] = useState({
    store: new CustomStore({
      key: 'customerId',
      loadMode: 'raw',
      load: () =>
        serviceCall.getData('Customer').then(response => response.data),
    }),
    sort: 'companyname',
  });

  const handleOnInitNewRow = useCallback((e: any) => {
    e.data.isActive = true;
  }, []);

  const handleOnRowUpdating = useCallback((e: any) => {
    e.newData = { ...e.oldData, ...e.newData };
  }, []);

  return (
    <>
      <div className="dataGrid1">
        <DataGrid
          id="SsoConfigDataGrid"
          dataSource={ssoConfigData}
          hoverStateEnabled={true}
          onInitNewRow={handleOnInitNewRow}
          onRowUpdating={handleOnRowUpdating}
          repaintChangesOnly={true}
          rowAlternationEnabled
          showBorders={true}>
          <Column
            allowEditing={false}
            caption="Record ID"
            dataField="pkId"
            visible={false}
          />
          <Column caption="Customer" dataField="customerId">
            <Lookup
              dataSource={customerLookupData}
              displayExpr="companyname"
              valueExpr="customerId"
            />
            <RequiredRule />
          </Column>
          <Column caption="Pool Name" dataField="poolName">
            <RequiredRule />
          </Column>
          <Column caption="Client ID" dataField="clientId">
            <RequiredRule />
          </Column>
          <Column caption="Customer Domain" dataField="customerDomain">
            <RequiredRule />
          </Column>
          <Column caption="Metadata Address" dataField="metadataAddress">
            <RequiredRule />
          </Column>
          <Column caption="Token Endpoint" dataField="tokenEndpoint">
            <RequiredRule />
          </Column>
          <Column caption="Is Active" dataType="boolean" dataField="isActive" />
          <Column
            allowEditing={false}
            caption="Created By"
            dataField="createdBy"
            visible={false}
          />
          <Column
            allowEditing={false}
            caption="Created Date"
            dataField="createdDate"
            dataType="date"
            visible={false}
          />
          <Column
            allowEditing={false}
            caption="Modified By"
            dataField="modifiedBy"
          />
          <Column
            allowEditing={false}
            caption="Modified Date"
            dataField="modifiedDate"
            dataType="date"
          />
          <Editing
            allowAdding={true}
            allowDeleting={true}
            allowUpdating={true}
            mode="popup"
            refreshMode="full">
            <Popup showTitle={true} title="SSO Configuration" />
            <Form>
              <Item dataField="customerId" />
              <Item dataField="poolName" />
              <Item dataField="customerDomain" />
              <Item dataField="clientId" />
              <Item dataField="metadataAddress" />
              <Item dataField="tokenEndpoint" />
              <Item dataField="isActive" />
            </Form>
          </Editing>
        </DataGrid>
      </div>
    </>
  );
};

export default SsoConfigPage;
